import { useState, useEffect, useRef } from 'react';
import { useTeam } from '../context/TeamContext';
import { shareEntryToTeam, unshareEntryFromTeam, fetchEntryShares, fetchTeamFolders } from '../lib/supabase';

export default function ShareToTeam({ entry, onShared }) {
  const { teams, activeTeam } = useTeam();
  const [open, setOpen]         = useState(false);
  const [shares, setShares]     = useState([]); // current shares for this entry
  const [folders, setFolders]   = useState({}); // { teamId: [folders] }
  const [selectedFolder, setSelectedFolder] = useState({});
  const [working, setWorking]   = useState({});
  const [loading, setLoading]   = useState(false);
  const ref = useRef(null);

  // Load current share state when opening
  useEffect(() => {
    if (!open || !entry?.id) return;
    setLoading(true);
    fetchEntryShares(entry.id)
      .then(setShares)
      .catch(console.error)
      .finally(() => setLoading(false));

    // Load folders for each team
    teams.forEach(async t => {
      const f = await fetchTeamFolders(t.id).catch(() => []);
      setFolders(prev => ({ ...prev, [t.id]: f }));
    });
  }, [open, entry?.id]);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!teams.length) return (
    <div style={{ fontSize: 12, color: 'var(--dim)', padding: '6px 0' }}>
      No teams yet —{' '}
      <a href="/team/create" style={{ color: 'var(--accent)', textDecoration: 'none' }}>create a team</a>
      {' '}to share prompts with teammates.
    </div>
  );

  const isSharedTo = (teamId) => shares.find(s => s.team_id === teamId);
  const shareStatus = (teamId) => shares.find(s => s.team_id === teamId)?.status;

  const handleShare = async (teamId) => {
    setWorking(w => ({ ...w, [teamId]: true }));
    try {
      const result = await shareEntryToTeam(entry.id, teamId, selectedFolder[teamId] || null);
      setShares(prev => {
        const existing = prev.find(s => s.team_id === teamId);
        if (existing) return prev.map(s => s.team_id === teamId ? { ...s, status: result.share.status } : s);
        return [...prev, { team_id: teamId, status: result.share.status }];
      });
      if (onShared) onShared(teamId, result.pending);
    } catch (e) { alert(e.message); }
    setWorking(w => ({ ...w, [teamId]: false }));
  };

  const handleUnshare = async (teamId) => {
    if (!window.confirm('Remove this entry from the team feed?')) return;
    setWorking(w => ({ ...w, [teamId]: true }));
    try {
      await unshareEntryFromTeam(entry.id, teamId);
      setShares(prev => prev.filter(s => s.team_id !== teamId));
    } catch (e) { alert(e.message); }
    setWorking(w => ({ ...w, [teamId]: false }));
  };

  const STATUS_COLORS = {
    approved: '#1d9e75',
    pending:  '#ef9f27',
    rejected: '#e24b4a',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: shares.length > 0 ? 'rgba(29,158,117,0.1)' : 'var(--surface2)',
          border: `0.5px solid ${shares.length > 0 ? 'rgba(29,158,117,0.4)' : 'var(--border2)'}`,
          borderRadius: 7, padding: '6px 12px',
          fontFamily: 'var(--font)', fontSize: 12.5,
          color: shares.length > 0 ? '#1d9e75' : 'var(--muted)',
          cursor: 'pointer', transition: 'all 0.12s',
          whiteSpace: 'nowrap',
        }}>
        <span style={{ fontSize: 13 }}>🏢</span>
        {shares.length > 0 ? `Shared to ${shares.length} team${shares.length > 1 ? 's' : ''}` : 'Share to team'}
        <span style={{ fontSize: 10, marginLeft: 2 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6,
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 10, zIndex: 100, minWidth: 260, maxWidth: 320,
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '0.5px solid var(--border)', fontSize: 11, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Share to team
          </div>

          {loading ? (
            <div style={{ padding: '14px', fontSize: 12, color: 'var(--dim)', textAlign: 'center' }}>Loading...</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {teams.map(t => {
                const shared    = isSharedTo(t.id);
                const status    = shareStatus(t.id);
                const busy      = working[t.id];
                const teamFolders = folders[t.id] || [];

                return (
                  <div key={t.id} style={{
                    padding: '12px 14px',
                    borderBottom: '0.5px solid var(--border)',
                  }}>
                    {/* Team name + status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: shared ? 0 : 8 }}>
                      <span style={{ fontSize: 14 }}>🏢</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1 }}>{t.name}</span>
                      {status && (
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 500,
                          background: `${STATUS_COLORS[status]}18`,
                          color: STATUS_COLORS[status],
                          border: `0.5px solid ${STATUS_COLORS[status]}40`,
                        }}>
                          {status === 'pending' ? '⏳ Pending' : status === 'approved' ? '✓ Shared' : '✗ Rejected'}
                        </span>
                      )}
                    </div>

                    {/* If not yet shared — show folder picker + share button */}
                    {!shared && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {teamFolders.length > 0 && (
                          <select
                            value={selectedFolder[t.id] || ''}
                            onChange={e => setSelectedFolder(prev => ({ ...prev, [t.id]: e.target.value || null }))}
                            style={{
                              background: 'var(--bg)', border: '0.5px solid var(--border2)',
                              borderRadius: 6, padding: '5px 8px', fontSize: 11,
                              color: 'var(--muted)', fontFamily: 'var(--font)', cursor: 'pointer',
                              width: '100%',
                            }}>
                            <option value="">No folder (general feed)</option>
                            {teamFolders.map(f => (
                              <option key={f.id} value={f.id}>
                                📁 {f.name}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          onClick={() => handleShare(t.id)}
                          disabled={busy}
                          style={{
                            background: '#7f77dd', color: '#fff', border: 'none',
                            borderRadius: 6, padding: '7px 12px',
                            fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500,
                            cursor: busy ? 'not-allowed' : 'pointer',
                            opacity: busy ? 0.6 : 1, width: '100%',
                          }}>
                          {busy ? 'Sharing...' : '+ Share to this team'}
                        </button>
                      </div>
                    )}

                    {/* If shared — show unshare option */}
                    {shared && (
                      <button
                        onClick={() => handleUnshare(t.id)}
                        disabled={busy}
                        style={{
                          marginTop: 7,
                          background: 'none', border: '0.5px solid var(--border2)',
                          borderRadius: 6, padding: '5px 10px', width: '100%',
                          fontFamily: 'var(--font)', fontSize: 11, color: 'var(--dim)',
                          cursor: busy ? 'not-allowed' : 'pointer',
                        }}>
                        {busy ? '...' : 'Remove from team'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}